import _ from "lodash";
import {
  fetchFlightSearchResults,
  retrieveSearchSessionMetadata,
  storeSearchSessionMetadata,
} from "../../api";
import { checkPackagePriceSearch } from ".";
import {
  FLIGHT_SEARCH_FORM_UPDATE,
  PACKAGE_FLIGHT_SEARCH_RESULT_SAVE,
  SINGLE_FLIGHT_SEARCH_CHANGE,
  FLIGHT_SEARCH_FILTER_UPDATE,
  SINGLE_FLIGHT_SEARCH_UPDATE,
  SINGLE_FLIGHT_SEARCH_RESET,
  SINGLE_FLIGHT_SEARCH_INIT,
  SINGLE_FLIGHT_SEARCH_LOADING,
  SINGLE_FLIGHT_SEARCH_IDLE,
  SINGLE_FLIGHT_FILTER_RESET_SINGLE,
  SINGLE_FLIGHT_FILTER_CHANGE,
} from "./types";
import { fetchSavedFlightSearchResults, searchFlights } from "../../api/flights";
import { DateTime } from "luxon";
import { toast } from "react-toastify";

export function flightSearchFormUpdate({ form }: { form: any }) {
  return { type: FLIGHT_SEARCH_FORM_UPDATE, form };
}

export const flightSearchResultsFetcher = () => async (dispatch, getState) => {
  const state = getState();
  const flight_sessions = _.get(state.TourPriceInfo, "search_data.search_statuses.flights", []);

  for (const idx in flight_sessions) {
    const session = flight_sessions[idx];
    const { dest_order } = session;
    const {
      session_id,
      search_payload: { search_uid },
    } = session;

    const response = await fetchFlightSearchResults({ session_id, search_uid });

    const flight = response?.results?.[0];
    flight.selected = true;
    flight.legs[0][0].selected = true;
    if (_.get(flight, "inbound_options", []).length) {
      flight.inbound_options[0].selected = true;
    }

    await dispatch(saveFlightSearchResult({ dest_order, data: flight, status: false }));
  }
  dispatch(checkPackagePriceSearch());
};

const saveFlightSearchResult = ({ dest_order, data, status }) => {
  return { type: PACKAGE_FLIGHT_SEARCH_RESULT_SAVE, dest_order, data, status };
};

// ====================== single flight actions ======================
function sglFlSearchPayloader({ form, source_entity, market_type, website_api_key }) {
  const output = {
    itinerary: [
      {
        origin: form.origin.origin_meta[0].iata,
        destination: form.destination.destination_meta[0].iata,
        departure: DateTime.fromISO(form.outbound_departure)
          .setZone("UTC", {
            keepLocalTime: true,
          })
          .toISO(),
      },
    ],
    origin: form.origin.origin_meta[0].iata,
    destination: form.destination.destination_meta[0].iata,
    outbound_departure: DateTime.fromISO(form.outbound_departure)
      .setZone("UTC", {
        keepLocalTime: true,
      })
      .toISO(),
    inbound_departure: form.inbound_departure
      ? DateTime.fromISO(form.inbound_departure)
          .setZone("UTC", {
            keepLocalTime: true,
          })
          .toISO()
      : null,
    adults: form.adults,
    children: form.children,
    baggage_only: form.baggage_only,
    stops: form.stops,
    flight_class: form.flight_class,
    api_key: window.btoa(source_entity),
    website_api_key,
    page_size: 100,
    ordering: "price",
    market_type,
  };

  if (form.isRoundTrip) {
    output.itinerary.push({
      origin: form.destination.destination_meta[0].iata,
      destination: form.origin.origin_meta[0].iata,
      departure: DateTime.fromISO(form.inbound_departure)
        .setZone("UTC", {
          keepLocalTime: true,
        })
        .toISO(),
    });
  }
  return output;
}

const singleFlSearchLoading = () => {
  return { type: SINGLE_FLIGHT_SEARCH_LOADING };
};

const singleFlSearchIdle = () => {
  return { type: SINGLE_FLIGHT_SEARCH_IDLE };
};

const singleFlSearchFailed = () => {
  return { type: SINGLE_FLIGHT_SEARCH_CHANGE, key: "failed", value: true };
};

export const updateSingleFlightSearchResult = (data) => {
  return { type: SINGLE_FLIGHT_SEARCH_UPDATE, data };
};

export const singleFlightSearch =
  ({ history, source_entity, market_type, website_api_key, setLoadingSearchBtn }) =>
  async (dispatch, getState) => {
    const state = getState();
    const search_form = state.FlightSearchForm;

    dispatch(singleFlSearchLoading());

    const payload = sglFlSearchPayloader({
      form: search_form,
      source_entity,
      market_type,
      website_api_key,
    });
    let results = await searchFlights(payload);
    results = results?.data;

    if (!results) {
      dispatch(singleFlSearchIdle());
      dispatch(singleFlSearchFailed());
      if (typeof setLoadingSearchBtn === "function") {
        setLoadingSearchBtn(false);
      }
      toast.warning("An unexpected error occurred. Please try again");
      history.push("/");
      return false;
    }

    const search_session_id = results?.session_id ?? "";
    const searchSessionResult = await storeSearchSessionMetadata({
      service_type: "FL",
      session_data: {
        search_session_id,
        search_form,
      },
    });

    if (_.isEmpty(searchSessionResult?.session_data ?? {})) {
      dispatch(singleFlSearchIdle());
      dispatch(singleFlSearchFailed());
      if (typeof setLoadingSearchBtn === "function") {
        setLoadingSearchBtn(false);
      }
      toast.warning("An unexpected error occurred. Please try again");
      history.push("/");

      return false;
    }

    const { session_id: page_session_id } = searchSessionResult;
    if (page_session_id) {
      history.push(`/flights/${page_session_id}`);
    }

    dispatch(singleFlSearchIdle());
    return true;
  };

export const fetchSglFlSearchResults =
  ({ history, source_entity, page_session_id }) =>
  async (dispatch) => {
    dispatch(singleFlSearchLoading());

    const session_meta = await retrieveSearchSessionMetadata({
      page_session_id,
    });
    if (!session_meta) {
      toast.warning("An unexpected error occurred. Please try again");
      history.push("/");
      return false;
    }

    const session_id = _.get(session_meta, "session_data.data.search_session_id");
    const search_form = _.get(session_meta, "session_data.data.search_form");

    if (!session_id) {
      toast.warning("An unexpected error occurred. Please try again");
      history.push("/");
      return false;
    }

    const api_key = window.btoa(source_entity);
    const response = await fetchSavedFlightSearchResults({
      session_id,
      api_key,
    });

    if (!response) {
      dispatch(singleFlSearchIdle());
      dispatch(singleFlSearchFailed());
      toast.warning("Could not retrieve any flights. Please try searching again");
      history.push("/");
      return false;
    }
    const flights = _.get(response, "results");
    const api_uid = _.get(response, "api_uid");

    dispatch(flightSearchFormUpdate({ form: search_form }));
    dispatch(
      updateSingleFlightSearchResult({
        flights,
        api_uid,
        page_session_id,
        session_id,
      }),
    );
    dispatch(singleFlSearchIdle());
    return true;
  };

// ==================== FLIGHT LIST FILTER ACTIONS ===================

export function updateFlightFilters(data: { [key: string]: string }) {
  return { type: FLIGHT_SEARCH_FILTER_UPDATE, data };
}

export function resetFlightFilter() {
  return { type: SINGLE_FLIGHT_SEARCH_RESET };
}

export function singleFilterReset({ key }: { key: string }) {
  return { type: SINGLE_FLIGHT_FILTER_RESET_SINGLE, key };
}

export function singleFilterResetPartially(values: { [key: string]: string }) {
  return { type: SINGLE_FLIGHT_SEARCH_INIT, values };
}

export function singleFilterSetOne({ key, value }: { key: string; value: string }) {
  return { type: SINGLE_FLIGHT_FILTER_CHANGE, key, value };
}
